import React, { Suspense, lazy } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes as RoutesReactRouterDom,
} from "react-router-dom";
import { RouteConstants } from "../constants/route-constant";
import TabLayout from "../components/Layout";
import MyQueries from "../components/Tables/MyQueryTable";
import PatientQuery from "../components/Tables/PatientQueryTable";
import EpicLaunch from "../components/launch/launch";
import QueryDetails from "../components/Shared/QueryDetails";
import MessageTable from "../components/Tables/MessageTable";
import MessageBox from "../components/Shared/MessageBox";
import { FhirClientPage } from "../components/TestEpic";
import TestCharts from "../components/dashboard";
import VBC from "../components/VBC";
import Unauthorized from "../components/unauthorized";
import CDI from "../components/CDI";
import ProtectedRoute from "./ProtectedRoute";
import EncounterDRG from "../components/EncounterDRG";
import ModuleNavigation from "../components/ModuleNavigation";
import UmQueryTable from "../components/Tables/UmQueryTable";
import CaireEmptyList from "../components/CAiRE/CaireEmptyList";
import PredictedDcDisposition from "../components/CAiRE/PredictedDcDisposition";
import ReadmissionRisk from "../components/CAiRE/ReadmissionRisk";
import DcBarriers from "../components/CAiRE/DcBarriers";
import ErrorBoundary from "../components/Shared/ErrorBoundary";
import UmReports from "../components/UM/UmReports";
import UmMyQueries from "../components/UM/UmMyQueries";
import UmMessages from "../components/UM/UmMessages";
import UmMessageDetails from "../components/UM/UmMessageDetails";
import UmAlerts from "../components/UM/UmAlerts";
import OpcdQueries from "../components/OPCD/Queries";
import OpcdHimAlerts from "../components/OPCD/HimAlerts";
import OpcdCodingAlerts from "../components/OPCD/CodingAlerts";
import OpcdCodingResults from "../components/OPCD/CodingResults";
import OpcdQueryDetails from "../components/OPCD/QueryDetails";
import ClinicalCareGuidelinesWorkspace from "../components/ClinicalCareGuidelines";
import EncoderWorkspace from "../components/Encoder";

const UmQueryDetails = lazy(() => import("../components/UM/UmQueryDetails"));

const withCaireErrorBoundary = (element: React.ReactNode) => (
  <ErrorBoundary title="Unable to load this CAiRE section. Try again or switch tabs.">
    {element}
  </ErrorBoundary>
);

const Routes: React.FC = () => {
  return (
    <Suspense fallback={<p>Loading...</p>}>
      <RoutesReactRouterDom>
        {/* Public routes */}
        <Route path='/launch' element={<EpicLaunch />} />
        <Route path='/unauthorized' element={<Unauthorized />} />

        {/* Protected routes */}
        <Route
          path='/'
          element={
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={RouteConstants.ROUTE_HOME} />} />
          <Route path={RouteConstants.ROUTE_HOME} element={<ModuleNavigation />} />
          <Route
            path={RouteConstants.ROUTE_CLINICAL_CARE_GUIDELINES}
            element={<ClinicalCareGuidelinesWorkspace />}
          />
          <Route
            path={RouteConstants.ROUTE_ENCODER}
            element={<EncoderWorkspace />}
          >
            <Route index element={<></>} />
            <Route path=":toolId" element={<></>} />
          </Route>
          <Route element={<TabLayout />}>
            <Route
              path={RouteConstants.ROUTE_CDI_MODULE}
              element={<PatientQuery />}
            />
            {/* Legacy redirects from renamed module paths */}
            <Route
              path='/arcplus'
              element={<Navigate to={RouteConstants.ROUTE_CDI_MODULE} replace />}
            />
            <Route
              path={RouteConstants.ROUTE_EPICTEST}
              element={<FhirClientPage />}
            />
            <Route
              path={RouteConstants.ROUTE_DASHBOARD}
              element={<TestCharts />}
            />
            <Route
              path={RouteConstants.ROUTE_MY_QUERIES}
              element={<MyQueries />}
            />
            <Route
              path={RouteConstants.ROUTE_MESSAGES}
              element={<MessageTable />}
            />
            <Route
              path={RouteConstants.ROUTE_MESSAGE_DETAILS}
              element={<MessageBox />}
            />
            <Route path={RouteConstants.ROUTE_CDI_ALERTS} element={<CDI />} />
            <Route
              path={RouteConstants.ROUTE_ENCOUNTER_DRG}
              element={<EncounterDRG />}
            />
            <Route
              path={RouteConstants.ROUTE_VBC_MODULE}
              element={<Navigate to={RouteConstants.ROUTE_VBC_ALERTS} replace />}
            />
            <Route
              path={RouteConstants.ROUTE_VBC_ALERTS}
              element={<VBC />}
            />
            {/* Legacy redirects from /hcc → /vbc */}
            <Route
              path='/hcc'
              element={<Navigate to={RouteConstants.ROUTE_VBC_MODULE} replace />}
            />
            <Route
              path='/hcc/vbc-alerts'
              element={<Navigate to={RouteConstants.ROUTE_VBC_ALERTS} replace />}
            />
            <Route
              path='/vbc-alerts'
              element={<Navigate to={RouteConstants.ROUTE_VBC_ALERTS} replace />}
            />
            <Route
              path={RouteConstants.ROUTE_OPCD_MODULE}
              element={
                <Navigate to={RouteConstants.ROUTE_OPCD_QUERIES} replace />
              }
            />
            <Route
              path={RouteConstants.ROUTE_OPCD_QUERIES}
              element={<OpcdQueries />}
            />
            <Route
              path={RouteConstants.ROUTE_OPCD_HIM_ALERTS}
              element={<OpcdHimAlerts />}
            />
            <Route
              path={RouteConstants.ROUTE_OPCD_CODING_ALERTS}
              element={<OpcdCodingAlerts />}
            />
            <Route
              path={RouteConstants.ROUTE_OPCD_CODING_RESULTS}
              element={<OpcdCodingResults />}
            />
            <Route
              path={RouteConstants.ROUTE_UM_MODULE}
              element={<Navigate to={RouteConstants.ROUTE_UM_QUERIES} replace />}
            />
            <Route
              path={RouteConstants.ROUTE_UM_QUERIES}
              element={<UmQueryTable />}
            />
            <Route
              path={RouteConstants.ROUTE_UM_REPORTS}
              element={<UmReports />}
            />
            <Route
              path={RouteConstants.ROUTE_UM_MY_QUERIES}
              element={<UmMyQueries />}
            />
            <Route
              path={RouteConstants.ROUTE_UM_MESSAGES}
              element={<UmMessages />}
            />
            <Route
              path={RouteConstants.ROUTE_UM_MESSAGE_DETAILS}
              element={<UmMessageDetails />}
            />
            <Route path={RouteConstants.ROUTE_UM_ALERTS} element={<UmAlerts />} />
            <Route
              path={RouteConstants.ROUTE_CAIRE_MODULE}
              element={
                <Navigate
                  to={RouteConstants.ROUTE_CAIRE_PREDICTED_DC_DISPOSITION}
                  replace
                />
              }
            />
            <Route
              path={RouteConstants.ROUTE_CAIRE_PREDICTED_DC_DISPOSITION}
              element={withCaireErrorBoundary(<PredictedDcDisposition />)}
            />
            <Route
              path={RouteConstants.ROUTE_CAIRE_READMISSION_RISK}
              element={withCaireErrorBoundary(<ReadmissionRisk />)}
            />
            <Route
              path={RouteConstants.ROUTE_CAIRE_DC_READINESS}
              element={withCaireErrorBoundary(<CaireEmptyList />)}
            />
            <Route
              path={RouteConstants.ROUTE_CAIRE_DC_BARRIERS}
              element={withCaireErrorBoundary(<DcBarriers />)}
            />
            {/* Legacy redirects from /quality → /caire */}
            <Route
              path='/quality'
              element={
                <Navigate to={RouteConstants.ROUTE_CAIRE_MODULE} replace />
              }
            />
            <Route
              path='/quality/physician-alerts'
              element={
                <Navigate
                  to={RouteConstants.ROUTE_CAIRE_PREDICTED_DC_DISPOSITION}
                  replace
                />
              }
            />
            <Route
              path='/quality/care-manager-alerts'
              element={
                <Navigate
                  to={RouteConstants.ROUTE_CAIRE_READMISSION_RISK}
                  replace
                />
              }
            />
            <Route
              path='/quality/pending-docs'
              element={
                <Navigate
                  to={RouteConstants.ROUTE_CAIRE_DC_READINESS}
                  replace
                />
              }
            />
            {/* Legacy redirects from previous CAiRE tab paths */}
            <Route
              path='/caire/physician-alerts'
              element={
                <Navigate
                  to={RouteConstants.ROUTE_CAIRE_PREDICTED_DC_DISPOSITION}
                  replace
                />
              }
            />
            <Route
              path='/caire/care-manager-alerts'
              element={
                <Navigate
                  to={RouteConstants.ROUTE_CAIRE_READMISSION_RISK}
                  replace
                />
              }
            />
            <Route
              path='/caire/pending-docs'
              element={
                <Navigate
                  to={RouteConstants.ROUTE_CAIRE_DC_READINESS}
                  replace
                />
              }
            />
          </Route>
        </Route>

        {/* Protected route outside layout */}
        <Route
          path={RouteConstants.ROUTE_QUERY_DETAILS}
          element={
            <ProtectedRoute>
              <QueryDetails />
            </ProtectedRoute>
          }
        />

        <Route
          path={RouteConstants.ROUTE_UM_QUERY_DETAILS}
          element={
            <ProtectedRoute>
              <UmQueryDetails />
            </ProtectedRoute>
          }
        />

        <Route
          path={RouteConstants.ROUTE_OPCD_QUERY_DETAILS}
          element={
            <ProtectedRoute>
              <OpcdQueryDetails />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path='*' element={<Navigate to={RouteConstants.ROUTE_HOME} />} />
      </RoutesReactRouterDom>
    </Suspense>
  );
};

export default Routes;
